import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

import LanguageSwitcher from "../misc/LanguageSwitcher";
import AmbientField from "./AmbientField";
import BrandSlides from "./BrandSlides";
import { useAuthMotion } from "./authMotion";
import styles from "./AuthLayout.module.css";

// The shared shell for every signed-out page: brand panel on the left, form
// column on the right, ambient motion behind the brand, and the entrance
// cascade for both halves.
//
// Login uses it today. ForgotPassword and SetPassword copy-paste this same
// brand column (ForgotPassword.js:145-164, SetPassword.js:78-104) and are
// meant to adopt this component next — which is why the page-specific parts
// are props rather than baked in.
//
// The outer <motion.section> carries the page-transition variants so that the
// AnimatePresence around the public routes in App.js has something to animate
// out. Its initial/animate are plain objects, not variant labels, so they
// don't propagate down and collide with the stagger containers below.
const AuthLayout = ({ title, subtitle, brandFooter, children }) => {
  const { t } = useTranslation(["auth"]);
  const v = useAuthMotion();

  return (
    <>
      {/* Outside the animated <section> on purpose. LanguageSwitcher is
          position:fixed, and an ancestor carrying a transform becomes the
          containing block for fixed descendants — which would pin it to the
          page instead of the viewport. Invisible on desktop, where the shell
          is exactly viewport-height; obvious on mobile, where it is taller. */}
      <LanguageSwitcher variant="floating" />

      {/* Enter-only: no `exit`. See the note on PublicRoutes in App.js — an
          AnimatePresence exit in this tree never completed, which stranded the
          outgoing page on screen. */}
      <motion.section
        className={styles.shell}
        initial={v.page.initial}
        animate={v.page.animate}
      >
        <motion.aside
          className={styles.brand}
          variants={v.brand}
          initial="hidden"
          animate="visible"
        >
          <AmbientField still={!v.ambient} />

          {/* Inherits "hidden"/"visible" from the panel above, so its own
              staggerChildren drives the cascade of the three blocks. */}
          <motion.div className={styles.brandInner} variants={v.container}>
            {/* The slide titles replace the single static brand.welcome
                heading; that key stays in auth.json because ForgotPassword
                and SetPassword still render it. */}
            <motion.div variants={v.item}>
              <BrandSlides />
            </motion.div>

            {/* App-store block. It lives on the dark panel rather than under
                the form so the form column stays a single short column. */}
            {brandFooter && (
              <motion.div variants={v.item}>{brandFooter}</motion.div>
            )}

            <motion.p className={styles.copy} variants={v.item}>
              {t("auth:brand.copyright", { year: new Date().getFullYear() })}
            </motion.p>
          </motion.div>
        </motion.aside>

        <div className={styles.formCol}>
          <motion.div
            className={styles.formInner}
            variants={v.container}
            initial="hidden"
            animate="visible"
          >
            {/* Small lockup, full colour -- the form column is white, which is
                the ground #292970's wordmark needs. */}
            <motion.img
              className={styles.formLogo}
              src="/dist/img/logo.png"
              alt="iPoultry"
              width="289"
              height="126"
              variants={v.item}
            />

            <motion.h1 className={styles.title} variants={v.item}>
              {title}
            </motion.h1>

            {subtitle && (
              <motion.p className={styles.subtitle} variants={v.item}>
                {subtitle}
              </motion.p>
            )}

            {/* Page form. Its own children opt into the cascade by declaring
                variants={v.item} — they inherit the "visible" label from here. */}
            {children}
          </motion.div>
        </div>
      </motion.section>
    </>
  );
};

export default AuthLayout;
